import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import { EmptyArray, sum } from 'lib/array/arrayUtils'
import KeyValueSection from '../SearchInputTriggerKeyValue'
import BodyText from 'LuxKit/Typography/BodyText'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import AIGlowText from '../AIGlowText'
import Caption from 'LuxKit/Typography/Caption'

const TravellerTextSection = styled(KeyValueSection)`
  padding-inline-end: ${rem(8)};
`

function Content({ showAIGlow }: { showAIGlow?: boolean }) {
  const intl = useIntl()
  const {
    occupancies = EmptyArray,
    disableSaleUnit,
  } = useContext(GlobalSearchStateContext)
  const containsChildren = occupancies.some(room => room.childrenAge?.length)
  const guestCount = useMemo(() => sum(occupancies, room => room.adults + (room.childrenAge?.length ?? 0)), [occupancies])

  const guestsLabel = intl.formatMessage({
    id: '0PyVbFYRn2Zo_9k9Oqj-',
    defaultMessage: 'Guests',
  })

  const selectAdultsChildrenLabel = intl.formatMessage({
    id: 'Y3QL1w1uSmK8CuH9qcty',
    defaultMessage: 'Select adults/children',
  })

  let travellerText: string
  if (!occupancies.length) {
    travellerText = selectAdultsChildrenLabel
  } else if (containsChildren && disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: 'vwTl8YQ9TNR15HigvEm_',
      defaultMessage: '{guestCount, plural, one {# guest} other {# guests}}',
    }, { guestCount })
  } else if (containsChildren) {
    travellerText = intl.formatMessage({
      id: 'f-UK1j-SHrxyrXQMOUiK',
      defaultMessage: '{guestCountLabel} ({roomCountLabel})',
    }, {
      guestCountLabel: intl.formatMessage({
        id: 'xoDjusWNzdzXcW_vFRbq',
        defaultMessage: '{guestCount, plural, one {# guest} other {# guests}}',
      }, { guestCount }),
      roomCountLabel: intl.formatMessage({
        id: 'eyOidaQq6Q94p3-JLdBP',
        defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
      }, { roomCount: occupancies.length }),
    })
  } else if (disableSaleUnit) {
    travellerText = intl.formatMessage({
      id: '4wpbgM3U6VbvprPl35Dc',
      defaultMessage: '{guestCount, plural, one {# adult} other {# adults}}',
    }, { guestCount })
  } else {
    travellerText = intl.formatMessage({
      id: 'XFBWUHguFMQw5sTWW8JC',
      defaultMessage: '{adultCountLabel} ({roomCountLabel})',
    }, {
      adultCountLabel: intl.formatMessage({
        id: 'feSHdxaM_K_zhclt4k4v',
        defaultMessage: '{guestCount, plural, one {# adult} other {# adults}}',
      }, { guestCount }),
      roomCountLabel: intl.formatMessage({
        id: 'eyOidaQq6Q94p3-JLdBP',
        defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
      }, { roomCount: occupancies.length }),
    })
  }

  return (
    <TravellerTextSection>
      <Caption colour="normal-contrast" variant="large" weight="bold">{guestsLabel}</Caption>
      <AIGlowText displayGlow={showAIGlow}>
        <BodyText variant="medium" colour="light-foreground-alt">
          {travellerText}
        </BodyText>
      </AIGlowText>
    </TravellerTextSection>
  )
}

export default Content
